/**
 * AI Assistant Configuration
 * Environment-based configuration for the AI Assistant widget
 * 
 * Feature: AI_ASSISTANT (feature-flags.ts)
 * - Enabled: LOCAL, DEV, BETA
 * - Disabled: GAMMA, PROD
 * 
 * Usage: Integrated in AppLayout.tsx via AIAssistantWidget
 * Button: "Ask AI" in CustomNavigationActions.tsx
 */
import type { AIAssistantConfig } from '@amzn/wwasft-components';

import { getCurrentStage } from '../../configuration/aws-exports';

/** Available agents with display names */
const AVAILABLE_AGENTS: AIAssistantConfig['availableAgents'] = [
  { id: 'rei_agent_core', name: 'Universal', appId: 'rei', description: 'Insights on Fixed Cost, Hydra and Daily Deep Dive (DDD)' },
  { id: 'fortune_agent_core', name: 'Fortune P&L', appId: 'fortune', description: 'Financials by business slice (e.g. Low ASP/High ASP, FC type etc.)' },
];

/** Quick prompts per agent - TODO: Remove when API provides these */
const QUICK_PROMPTS_BY_AGENT: AIAssistantConfig['quickPromptsByAgent'] = {
  rei_agent_core: [
    { text: 'What input variables are driving the difference between high and low ASP CPU?', fullPrompt: 'What input variables are driving the difference between high and low ASP CPU?', category: 'Hydra / Cost Analytics' },
    { text: 'What are the fixed costs for Q4 2025 compared to prior quarter?', fullPrompt: 'What are the fixed costs for Q4 2025 compared to prior quarter?', category: 'Fixed Cost' },
    { text: 'Show me the performance report for BCN1 yesterday', fullPrompt: 'Show me the performance report for BCN1 yesterday', category: 'Daily Deep Dive' },
  ],
  fortune_agent_core: [
    { text: 'UK GMS for October 2025', fullPrompt: 'What is UK GMS for October 2025?' },
    { text: "EU5's OPPU for 2024", fullPrompt: "Show me EU5's OPPU for 2024." },
    { text: 'OI trend for NA YTD 2025', fullPrompt: 'What is the OI trend for NA YTD 2025?' },
    { text: 'Low ASP trending for JP', fullPrompt: 'How is Low ASP trending for JP in 2025?' },
    { text: 'EE profitability in US', fullPrompt: 'Show me EE profitability in the US for 2025.' },
  ],
};

/** Stage-specific URLs */
const STAGE_URLS: Record<string, { socketUrl: string; serviceUrl: string }> = {
  alpha: {
    socketUrl: 'wss://us-east-1-alpha-chat-ws.atlas.taxaudits.amazon.dev',
    serviceUrl: 'https://us-east-1-alpha-chat-rest.atlas.taxaudits.amazon.dev/api/v1/',
  },
  beta: {
    socketUrl: 'wss://us-east-1-beta-chat-ws.atlas.taxaudits.amazon.dev',
    serviceUrl: 'https://us-east-1-beta-chat-rest.atlas.taxaudits.amazon.dev/api/v1/',
  },
  gamma: {
    socketUrl: 'wss://us-east-1-gamma-chat-ws.atlas.taxaudits.amazon.dev',
    serviceUrl: 'https://us-east-1-gamma-chat-rest.atlas.taxaudits.amazon.dev/api/v1/',
  },
  prod: {
    socketUrl: 'wss://us-east-1-prod-chat-ws.atlas.taxaudits.amazon.dev',
    serviceUrl: 'https://us-east-1-prod-chat-rest.atlas.taxaudits.amazon.dev/api/v1/',
  },
};

/** Disclaimer with T&C link */
const DISCLAIMER = {
  text: 'You may need to verify responses for accuracy.',
  linkText: 'FinSpace AI Assistant T&C',
  linkUrl: 'https://w.amazon.com/bin/view/WWCF_IPAT/FinTech/CoreEngineering/Teams/FinSpace/AIAssistantT&C',
};

/** Shared config across all stages */
const SHARED_CONFIG = {
  title: 'FinSpace',
  quickPromptsByAgent: QUICK_PROMPTS_BY_AGENT,
  disclaimer: DISCLAIMER,
};

/** Default stage if not specified */
const DEFAULT_STAGE = 'beta';

/** Partial config without runtime values (getAuthToken, userAlias) */
type AIAssistantEnvConfig = Pick<
  AIAssistantConfig,
  'socketUrl' | 'serviceUrl' | 'quickPromptsByAgent' | 'title' | 'availableAgents' | 'disclaimer'
>;

/**
 * Get AI Assistant configuration based on current environment
 */
export const getAIAssistantConfig = (): AIAssistantEnvConfig => {
  const stage = getCurrentStage() || DEFAULT_STAGE;
  const urls = STAGE_URLS[stage] || STAGE_URLS[DEFAULT_STAGE];
  return { ...SHARED_CONFIG, ...urls, availableAgents: AVAILABLE_AGENTS };
};
